// Blog content is authored as `blocks` (see BlogPost.jsx Block renderer).
// Block types: md | graphic | keypoints | steps | callout | compare.
// Graphic names map to BlogGraphics.jsx: pillarmap, planner, execute, habits, library, journal.

const blogs = [
  /* ───────────── HOW TO DO A WEEKLY REVIEW (SEO) ───────────── */
  {
    title: "How to Do a Weekly Review in 20 Minutes (Without a Complicated System)",
    slug: "how-to-do-a-weekly-review",
    date: "October 15, 2026",
    isoDate: "2026-10-15",
    readTime: "6 min read",
    cluster: "Planning",
    ctaLink: "/dashboard/planning",
    ctaText: "Set up your weekly plan →",
    keywords: [
      "how to do a weekly review",
      "weekly review questions",
      "weekly review template",
      "weekly planning routine",
      "personal weekly review",
    ],
    excerpt: "A weekly review is 20 minutes that keeps your goals connected to your actual week. Here's a simple five step routine, the questions to ask, and what to do when you skip a week.",
    blocks: [
      { type: 'md', value: `## What a weekly review is for

Plans go stale fast. You set a goal, break it into steps, and then a busy week happens. Tasks pile up, priorities shift, and by Friday you're reacting to whatever is loudest.

A weekly review is a short, fixed meeting with yourself to fix that. Once a week you look at what happened, check that your goals still have a next step, and decide what next week is for. That's it.

It doesn't need a special notebook or a 40 item checklist. The version below takes about 20 minutes. Short is the point: a review you actually do every week beats a perfect one you do twice.` },
      { type: 'callout', variant: 'note', title: 'The core idea', text: "Every week, reconnect your goals to your calendar. If a goal has no action next week, it's paused, whether you meant it or not." },
      { type: 'md', value: `## When to do it

Pick one slot and keep it. Common choices:

- **Friday afternoon:** the week is fresh in your mind and you start Monday with a plan.
- **Sunday evening:** calmer, and it sets the tone for the week.
- **Monday morning:** works if your weekends are unpredictable.

The best time is the one you won't skip. Put it in your calendar as a repeating event, with a reminder.` },
      { type: 'steps', items: [
        { title: "1. Clear (3 minutes)", text: "Get loose ends out of your head: notes, open tabs, things you promised people. Write them down or put them on your todo list." },
        { title: "2. Look back (5 minutes)", text: "What got done this week? What didn't, and why? Look at your calendar and finished tasks instead of relying on memory." },
        { title: "3. Check each goal (5 minutes)", text: "For every active goal: did it move? Is the next step still clear? Is it still a priority, or should it be paused on purpose?" },
        { title: "4. Pick next week's few actions (5 minutes)", text: "One to three actions per goal at most. Choose the one that matters most if the week goes badly." },
        { title: "5. Put them on the calendar (2 minutes)", text: "Give each action a day and a time. An action without a slot is still a wish." },
      ] },
      { type: 'md', value: `## The questions to ask

If you only remember one part of this guide, keep these questions. Answer them in writing, even in one line each:

- **What went well this week?**
- **What didn't happen that I planned?**
- **What got in the way?** Time, energy, unclear next step, something else?
- **Is each goal still worth working on right now?**
- **What is the single most important thing for next week?**
- **When exactly will I do it?**

The third question is where most of the learning is. If the same thing keeps getting in the way (no time slot, steps too big, too many goals), change the plan instead of trying harder.` },
      { type: 'compare', left: {
        title: "A review that fades",
        items: [
          "A long checklist that takes an hour",
          "Done whenever there's time",
          "Only looks at tasks, not goals",
          "Ends with a longer todo list",
        ],
      }, right: {
        title: "A review that sticks",
        items: [
          "Five steps, about 20 minutes",
          "Same day and time every week",
          "Checks that each goal has a next step",
          "Ends with a few actions on the calendar",
        ],
      } },
      { type: 'md', value: `## What to do when a goal didn't move

It happens to everyone. A goal sits still for a week, then two. The weekly review is where you deal with it honestly, with one of three choices:

- **Make the next step smaller.** "Work on the portfolio" becomes "pick the three projects to include". If you can't start it in 15 minutes, it's too big. Our guide on [how to break down a big goal](/blog/how-to-break-down-a-big-goal) shows how to get from a goal to steps that size.
- **Give it a slot.** Many goals don't fail, they just never get a time. Put it on the calendar next week.
- **Pause it on purpose.** If something else matters more right now, say so and write down when you'll look at it again. A paused goal is fine. A goal you feel guilty about every week but never touch just drains energy.

## What to do when you skip a week

You will miss a review at some point. Don't try to catch up on two weeks at once. Just do the next one, and keep it short. The same rule from building habits applies: missing once is normal, missing twice is the start of a new pattern. More on that in [how to stick to your goals when motivation runs out](/blog/how-to-stick-to-your-goals).` },
      { type: 'callout', variant: 'tip', title: 'Make it easy to start', text: "Keep your goals and their next steps in one place, so the review is looking, not hunting. If you have to dig through three apps first, you'll skip it." },
      { type: 'md', value: `## Where Dmyor fits

A weekly review is easiest when your goals, milestones and next actions already live together. In Dmyor each goal is a vision on a visual canvas, broken into milestones and actions, with habits for the daily parts and a daily view of what's actionable today. In your review you open the canvas, see which visions moved, and set next week's actions. It's free to start.

Whatever you use, block 20 minutes this week, ask the six questions, and leave with a few actions on your calendar.` },
    ],
  },
  /* ───────────── HOW TO SET GOALS FOR 2027 (SEO, seasonal) ───────────── */
  {
    title: "How to Set Goals for 2027 (and Still Be Working on Them in March)",
    slug: "how-to-set-goals-for-2027",
    date: "October 14, 2026",
    isoDate: "2026-10-14",
    readTime: "7 min read",
    cluster: "Planning",
    ctaLink: "/dashboard/planning",
    ctaText: "Plan your 2027 →",
    keywords: [
      "how to set goals for 2027",
      "2027 goals",
      "goal setting for the new year",
      "new year goals that stick",
      "how to plan your year",
    ],
    excerpt: "Most new year goals are gone by February. Here's a simple way to set 2027 goals: review 2026, pick a few, plan the first 90 days, and put this week on the calendar.",
    blocks: [
      { type: 'md', value: `## Why new year goals fade

Most people set goals the same way every year: a burst of motivation in late December, a list of big intentions, and then normal life comes back in January. By February the list is forgotten.

The problem is rarely the goals themselves. It's the gap between the goal and the week. "Get fit", "grow my savings" or "launch the side project" are directions, not plans. Nothing on that list tells you what to do on a tired Tuesday in the second week of January.

So this guide is less about choosing the perfect goals and more about building the bridge: from the year, to the quarter, to this week.` },
      { type: 'callout', variant: 'note', title: 'The core idea', text: "A yearly goal only works if it reaches all the way down to something you'll do this week." },
      { type: 'md', value: `## Step 1: Review 2026 before you plan 2027

Spend 30 minutes looking back before you look forward. Answer four questions in writing:

- **What went well?** What are you proud of, even if it was small?
- **What didn't happen?** Which goals quietly disappeared, and when?
- **Why did they stop?** Too big, no time slot, lost interest, life got in the way?
- **What do I want more of, and less of?**

The third question is the most useful one. If your goals usually die in week three because there was never a set time for them, that's the thing your 2027 plan has to fix.` },
      { type: 'md', value: `## Step 2: Pick fewer goals than feels comfortable

Life has many areas: work, money, health, relationships, learning, fun. You can't push on all of them at once.

Choose **one to three** goals that would make the biggest difference to your year. Everything else gets maintenance, not a project. It feels like you're giving things up. You are, and that's what makes the remaining goals possible.

For each goal, write it so you'd know at the end of the year whether it happened:

- Vague: "Get healthier."
- Clear: "Run a 10k by October 2027, and be running three times a week by March."` },
      { type: 'compare', left: {
        title: "A wish list",
        items: [
          "Eight goals across every area",
          "Written once, in December",
          "Big outcomes, no first step",
          "Reviewed again next December",
        ],
      }, right: {
        title: "A plan for the year",
        items: [
          "One to three goals that matter most",
          "Broken into quarters and weeks",
          "A first action you can do this week",
          "Reviewed every week, adjusted every quarter",
        ],
      } },
      { type: 'md', value: `## Step 3: Plan the first 90 days, not the whole year

Twelve months is too far away to plan in detail, and it makes it easy to tell yourself there's plenty of time. A quarter is close enough to feel real.

For each goal, ask: **what has to be true by the end of March?** That's your first milestone. Then break it into a few smaller steps for January, February and March.

Leave the rest of the year rough on purpose. You'll know much more in April than you do now, and you can plan the next quarter then. If you want the full method for splitting a goal into milestones and actions, our guide on [how to break down a big goal](/blog/how-to-break-down-a-big-goal) walks through it.` },
      { type: 'steps', items: [
        { title: "2027 goal", text: "Launch my side business and get the first 10 paying customers by the end of the year." },
        { title: "By end of March", text: "Have a simple offer, a one-page site, and 5 conversations with potential customers done." },
        { title: "January", text: "Write down who it's for and what problem it solves. Talk to 2 people who have that problem." },
        { title: "First week of January", text: "Book two 30 minute calls. Block Tuesday and Thursday evenings, 8 to 9pm, for the business." },
        { title: "Weekly review", text: "Sunday, 10 minutes: what moved, what's next week's one most important action?" },
      ] },
      { type: 'callout', variant: 'tip', title: 'This is an example', text: "Use your own goal. The shape stays the same: the year, the first quarter, the first month, and two actions for the first week." },
      { type: 'md', value: `## Step 4: Give each goal a time and a minimum

Goals compete with everything else in your week, and "when I have time" usually means never. For each goal:

- **Pick a fixed slot.** A day and a time, written down. "Tuesday 8pm" beats "this week sometime".
- **Set a minimum version.** On a bad week, what's the smallest thing that still counts? One run instead of three. One email instead of a whole afternoon.
- **Turn the daily part into a habit.** If the goal depends on something you do most days, track it as a habit so you can see the streak.

The minimum is what keeps a goal alive through the weeks when life gets in the way. Our guide on [how to stick to your goals when motivation runs out](/blog/how-to-stick-to-your-goals) goes deeper on this part.` },
      { type: 'md', value: `## Step 5: Start before January 1st

There's nothing special about January 1st except that everyone else is starting too. If you set your goals in October, November or December, take the first small action now. A one week head start is worth more than a perfect plan written on New Year's Eve.

Then set a reminder for the end of March to review the quarter and plan the next one.` },
      { type: 'md', value: `## Where to keep your 2027 plan

A year of goals has a lot of layers: the goals themselves, the quarterly milestones, the weekly actions and the habits underneath. It helps to keep them in one place, so the small thing you do on a Tuesday stays connected to the bigger reason.

That's what Dmyor is built for. You write each goal as a vision, break it into milestones and actions on a visual canvas, track the daily parts as habits, and work from a daily view of what's actionable today. It's free to start.

Whatever you use, don't stop at the list. Put the first action for each goal on your calendar this week.` },
    ],
  },

  /* ───────────── CAREER CHANGE PLAN (SEO) ───────────── */
  {
    title: "How to Make a Career Change Plan (That You Can Start This Week)",
    slug: "how-to-make-a-career-change-plan",
    date: "October 13, 2026",
    isoDate: "2026-10-13",
    readTime: "8 min read",
    cluster: "Planning",
    ctaLink: "/dashboard/planning",
    ctaText: "Start your plan →",
    keywords: [
      "career change plan",
      "how to make a career change plan",
      "how to plan a career change",
      "career transition plan",
      "how to switch careers step by step",
    ],
    excerpt: "Most career changes stall between \"I want out\" and \"I applied\". Here's a plan that turns the switch into small tests, clear milestones and one action for this week.",
    blocks: [
      { type: 'md', value: `## Why career changes stall

Wanting a different career is common. Actually switching is much rarer, and the gap is rarely about talent. It usually looks like this:

- **The target is fuzzy.** "Something more creative" or "anything but this" can't be planned.
- **The jump feels all or nothing.** Quit, retrain, start over. That is scary enough to postpone forever.
- **Research replaces action.** Months of reading about a field without ever talking to someone in it or doing a piece of the work.
- **There's no timeline.** Without milestones, "someday" quietly becomes "never".

A good career change plan fixes all four. It names a specific target, turns the jump into a series of small tests, and puts a real action on your calendar this week.` },
      { type: 'callout', variant: 'note', title: 'The core idea', text: "Don't plan the leap. Plan the tests that make the leap small." },
      { type: 'md', value: `## Step 1: Write down what you're moving toward, not just away from

Start with two short lists:

- **Keep:** what you want to carry into the next role. Skills you enjoy using, the kind of people you like working with, the pay or flexibility you need.
- **Change:** what has to be different. The work itself, the pace, the industry, the level of autonomy.

Then write one to three candidate directions that fit both lists. Be concrete: "UX researcher at a software company" works, "something in tech" does not. You don't have to be sure yet. You just need something specific enough to test.` },
      { type: 'md', value: `## Step 2: Test before you leap

Each candidate direction is a guess. Before you bet a year on it, run small, cheap tests that give you real information:

- **Talk to people doing the job.** Ask what a normal week looks like, what they wish they'd known, and how they got in. A few conversations will teach you more than weeks of reading.
- **Do a small piece of the work.** Write the article, design the screen, analyse the dataset, run the workshop. A weekend project tells you whether you enjoy the work, not just the idea of it.
- **Take one short course or tutorial.** Not a full degree. Just enough to see if the learning feels energising or draining.

After each test, write two lines: what you learned, and whether this direction feels more or less right. Drop directions that fail. Double down on the one that keeps pulling you in.` },
      { type: 'compare', left: {
        title: "Career change as a leap",
        items: [
          "Quit first, figure it out later",
          "One huge, scary decision",
          "Months of research, no real feedback",
          "Easy to postpone indefinitely",
        ],
      }, right: {
        title: "Career change as a series of tests",
        items: [
          "Test while you still have income",
          "Many small, reversible steps",
          "Real conversations and real work samples",
          "Something to do every week",
        ],
      } },
      { type: 'md', value: `## Step 3: Map the gap

Once one direction stands out, compare where you are with what the role actually needs. Look at real job posts for the role and list the skills and proof that keep coming up.

Sort the gap into three groups:

- **Already have:** skills that transfer. Say these out loud in your CV and conversations.
- **Can show quickly:** things one or two projects would prove.
- **Need real time:** skills or credentials that take months.

This list is the raw material for your milestones. It also stops you from over-preparing: you don't need everything on the list before you start applying.` },
      { type: 'md', value: `## Step 4: Turn it into milestones and dates

Now work backwards from a target date. Pick something realistic for your situation, then ask what has to be true before that.

A typical shape:

1. **Direction confirmed:** you've done the tests and picked one path.
2. **Proof built:** two or three portfolio pieces or projects that show you can do the work.
3. **Network started:** a handful of people in the field who know what you're aiming for.
4. **Applications out:** a steady weekly rhythm of tailored applications or pitches.
5. **Offer and transition:** the role, plus a plan for leaving your current job well.

Only plan the nearest milestone in detail. The later ones will change as you learn, and that's fine. If you want the general method, we wrote a guide on [how to break down a big goal](/blog/how-to-break-down-a-big-goal) into milestones and weekly actions.` },
      { type: 'steps', items: [
        { title: "Goal", text: "Move from customer support into UX research within about a year." },
        { title: "Nearest milestone", text: "Decide whether UX research is right, by the end of next month." },
        { title: "This month's tests", text: "Talk to 3 UX researchers. Do one small usability study on an app you use. Take one short intro course." },
        { title: "This week", text: "Message 2 people on LinkedIn asking for a 20 minute chat. Pick the app for the mini study." },
        { title: "Weekly rhythm", text: "Two evenings a week, 7 to 8pm. Sunday: 10 minute review of what you learned and next week's actions." },
      ] },
      { type: 'callout', variant: 'tip', title: 'This is an example', text: "Swap in your own direction. The shape stays the same: one clear target, small tests, the nearest milestone, and actions for this week." },
      { type: 'md', value: `## Step 5: Protect the plan from your real week

Career changes usually happen on top of a full-time job, so the plan has to survive tired evenings.

- **Fix the time.** Two or three set slots a week beat "whenever I have energy".
- **Set a minimum.** On a bad week, one message or 20 minutes on a project still counts.
- **Review weekly.** Ten minutes: what did I learn, what's next, is the target still right?

If keeping going is the hard part for you, our guide on [how to stick to your goals when motivation runs out](/blog/how-to-stick-to-your-goals) goes deeper on this.` },
      { type: 'md', value: `## Where to keep the plan

A career change has a lot of moving parts: directions to test, conversations to book, projects to build, applications to send. It helps to keep the goal, its milestones and this week's actions in one place, so the small task tonight stays connected to why you're doing it.

That's what Dmyor is built for. You write the change as a vision, break it into milestones and actions on a visual canvas, track the weekly rhythm as a habit, and work from a daily view of what's actionable today. It's free to start.

Whatever tool you use, put one action on the calendar this week. A career change plan with nothing scheduled is still just a wish.` },
    ],
  },
  /* ───────────── HOW TO STICK TO YOUR GOALS (SEO) ───────────── */
  {
    title: "How to Stick to Your Goals When Motivation Runs Out",
    slug: "how-to-stick-to-your-goals",
    date: "October 12, 2026",
    isoDate: "2026-10-12",
    readTime: "7 min read",
    cluster: "Planning",
    ctaLink: "/dashboard/planning",
    ctaText: "Start your plan →",
    keywords: [
      "how to stick to your goals",
      "why can't I stick to my goals",
      "how to stay consistent with goals",
      "how to stop giving up on goals",
      "how to keep going when motivation fades",
    ],
    excerpt: "You don't quit goals because you're lazy. You quit because the plan was built for your most motivated day. Here's how to build one for your worst week instead.",
    blocks: [
      { type: 'md', value: `## Why you keep dropping goals

The pattern is familiar. You set a goal, the first week goes great, then something happens: a busy week, a bad night, a missed day. The streak breaks, the goal starts to feel heavy, and a month later you quietly stop thinking about it.

It's tempting to call that a discipline problem. Usually it isn't. Most goals are dropped for boring, fixable reasons:

- **The goal was never broken down.** "Get fit" or "launch the business" gives you nothing to do on a Tuesday evening, so on Tuesday evening you do nothing.
- **The plan assumed your best self.** It needed an hour a day, every day, at full energy. Real weeks don't look like that.
- **One miss turned into a stop.** Missing a day felt like failing, so the goal got abandoned instead of resumed.
- **Nobody looked at it again.** No review means no adjustment, and an unadjusted plan drifts away from your real life.

Each of these has a practical fix. None of them needs more willpower.` },
      { type: 'callout', variant: 'note', title: 'The core idea', text: "Motivation starts goals. Structure keeps them going. Build the plan for the week when you don't feel like it." },
      { type: 'md', value: `## 1. Make the next step obvious

A goal you can't act on today is a goal you'll postpone today. So the first job is to turn it into something concrete: a few milestones that prove progress, and a short list of actions small enough to finish in one sitting.

Test each action: would you know exactly where to start, and would you know when it's done? "Work on the portfolio" fails. "Write the intro paragraph for case study one" passes.

If you want the full method, we wrote a step by step guide on [how to break down a big goal](/blog/how-to-break-down-a-big-goal).` },
      { type: 'md', value: `## 2. Set a minimum, not just a target

Every goal needs two levels:

- **The target:** what a good week looks like. Four runs. Ten pages written. Five job applications.
- **The minimum:** what still counts on a terrible week. One short run. One paragraph. One application.

The minimum is what keeps the goal alive when life gets loud. It feels almost too small to matter, and that is the point: it's the version you can always do, so the chain doesn't fully break. Many weeks you'll do more once you've started. On the bad ones, the minimum is enough.` },
      { type: 'compare', left: {
        title: "Plan built for your best week",
        items: [
          "One big target, all or nothing",
          "Needs high energy every day",
          "A missed day feels like failure",
          "Quietly abandoned after a rough week",
        ],
      }, right: {
        title: "Plan built for your worst week",
        items: [
          "A target and a minimum",
          "Small actions you can always start",
          "A missed day is just a missed day",
          "Resumed after a rough week",
        ],
      } },
      { type: 'md', value: `## 3. Decide when and where, in advance

"I'll study more this week" is a hope. "Monday and Thursday at 7pm, at the kitchen table, I'll do one lesson" is a plan. Deciding the time and place ahead of time removes the daily negotiation with yourself, which is where most good intentions lose.

It also helps to plan for the obvious obstacle. Write it as an if-then: "If I get home too tired to run, then I'll walk for 15 minutes instead." You already know what usually derails you. Decide now what you'll do when it happens.` },
      { type: 'md', value: `## 4. Turn the repeatable part into a habit

Most goals have a part that repeats: writing, training, practicing, reaching out. That part works better as a habit with a small daily or weekly target than as a one-off task you keep rescheduling.

Keep it to one or two habits per goal. A wall of ten new habits is the same trap as a plan built for your best self.` },
      { type: 'md', value: `## 5. Never miss twice

You will miss days. Everyone does. The goal doesn't die on the first miss. It dies on the second and third, when "I missed yesterday" becomes "I guess I stopped."

So make one simple rule: **a miss is fine, two in a row is not.** After a missed day, the only job is to do the minimum the next day. No catching up, no punishing double session. Just resume.` },
      { type: 'md', value: `## 6. Review every week (10 minutes)

A weekly review is what keeps a plan matched to your real life. Once a week, ask:

- What did I actually do?
- What got in the way?
- Is the target still right, or should I lower it for now?
- What are the one or two actions for next week, and when will I do them?

Adjusting the plan isn't giving up. A goal you scale down and keep is worth far more than a perfect plan you abandon. For a fuller routine with the exact questions to ask, see [how to do a weekly review in 20 minutes](/blog/how-to-do-a-weekly-review).` },
      { type: 'steps', items: [
        { title: "Goal", text: "Run a 10k by spring." },
        { title: "Milestone", text: "Run 5k without stopping by the end of next month." },
        { title: "Target and minimum", text: "Target: 3 runs a week. Minimum: one 15 minute run or walk." },
        { title: "When and where", text: "Tuesday and Thursday 7am before work, Sunday morning in the park. If it rains: 15 minutes of indoor exercise instead." },
        { title: "Rule", text: "Never miss twice. Sunday: 10 minute review and plan next week's runs." },
      ] },
      { type: 'callout', variant: 'tip', title: 'This is an example', text: "Swap in your own goal. The shape stays the same: a clear next step, a minimum, a when and where, and a short weekly review." },
      { type: 'md', value: `## Where to keep it

Sticking to a goal is much easier when the goal, its milestones and today's actions sit in one place, so the small task you're doing tonight stays connected to why you're doing it.

That's what Dmyor is built for. You write the goal as a vision, break it into milestones and actions on a visual canvas, track the repeatable part as a habit with a streak, and work from a daily view that shows only what's actionable today. It's free to start.

Whatever you use, build the plan for your worst week. Your best weeks will take care of themselves.` },
    ],
  },
  /* ───────────── HOW TO PLAN YOUR LIFE IN YOUR 20S (SEO) ───────────── */
  {
    title: "How to Plan Your Life in Your 20s (Without Pretending You Know Everything)",
    slug: "how-to-plan-your-life-in-your-20s",
    date: "October 11, 2026",
    isoDate: "2026-10-11",
    readTime: "7 min read",
    cluster: "Planning",
    ctaLink: "/dashboard/planning",
    ctaText: "Start your plan →",
    keywords: [
      "how to plan your life in your 20s",
      "life plan in your 20s",
      "how to plan your life",
      "what to do with my life in my 20s",
      "life planning for young adults",
    ],
    excerpt: "You don't need a 10 year roadmap at 24. You need a direction, a few bets, and something to do this week. Here's a plan built for not knowing yet.",
    blocks: [
      { type: 'md', value: `## The problem with planning your 20s

Most life planning advice assumes you already know what you want. In your 20s you usually don't, or you think you do and it changes in eighteen months. So you either write a rigid plan that breaks the first time life surprises you, or you skip planning entirely and drift.

Both feel bad. The rigid plan makes every change feel like failure. The drift makes years go by without much to show for them.

There's a middle path: plan for **direction and learning**, not for certainty. Treat the next few years as a series of bets you place on purpose, then check what you learned.` },
      { type: 'callout', variant: 'note', title: 'The mindset', text: "In your 20s a good plan isn't one you follow perfectly. It's one that helps you find out faster what you actually want." },
      { type: 'md', value: `## Step 1: Write a direction, not a destination

Skip "where do I want to be in 10 years?". It's a hard question to answer honestly at 23. Ask something smaller instead:

- What do I want more of in my life three years from now?
- What do I want less of?
- What would I be proud to have tried, even if it didn't work?

Turn the answers into one or two sentences. For example: "I want work that uses my hands and my head, money that doesn't keep me up at night, and to live somewhere I chose rather than somewhere I ended up." That's a direction. It's vague on purpose. It still rules a lot of things out.` },
      { type: 'md', value: `## Step 2: Pick the few areas that matter right now

Life has many areas: career, money, health, relationships, where you live, learning, fun. You can't push on all of them at once, and you don't need to.

Choose **two or three** that would make the biggest difference this year. For many people in their 20s that's some mix of career (getting skills and a foothold), money (getting out of the red or building a buffer) and health (habits that are easier to build now than later). The others still matter. They just get maintenance, not a project.` },
      { type: 'graphic', name: 'pillarmap' },
      { type: 'md', value: `## Step 3: Turn each area into a bet

A bet is a goal with an honest question attached. Instead of "become a designer", write:

**"For the next 6 months I'll build three real design projects, to find out if I enjoy this work enough to switch careers."**

That framing does two things. It gives you a concrete goal you can plan. And it removes the pressure of having to be right, because a bet that teaches you "no, not this" is still a win. You just saved yourself years of wondering.

Give each bet a time box: three, six or twelve months. Long enough to learn something real, short enough that you'll actually review it.

If one of your bets is a career switch, our guide on [how to make a career change plan](/blog/how-to-make-a-career-change-plan) walks through testing a new direction before you leap.` },
      { type: 'steps', items: [
        { title: "Direction", text: "Work that's creative and practical, more control over my time, live near the sea by 30." },
        { title: "Bet 1: career", text: "In 6 months, finish 3 portfolio design projects to test whether I want to switch from accounting." },
        { title: "Bet 2: money", text: "In 12 months, save 3 months of expenses so a career switch is possible, not scary." },
        { title: "Bet 3: health", text: "In 3 months, go from no exercise to running 3 times a week." },
        { title: "This week", text: "Pick the first portfolio project and sketch it. Set up an automatic transfer to savings. Do two 20 minute runs." },
      ] },
      { type: 'callout', variant: 'tip', title: 'This is an example', text: "Your direction and bets will look nothing like this. The shape is what matters: one direction, two or three bets, and actions you can do this week." },
      { type: 'md', value: `## Step 4: Break each bet down to this week

A bet without next steps is a wish with a deadline. For each one, ask: what has to be true halfway through? What has to be true at the end of this month? Then pick one or two actions for this week and give them a time on your calendar.

If you want a full method for this part, we wrote a guide on [how to break down a big goal](/blog/how-to-break-down-a-big-goal) into milestones and weekly actions.` },
      { type: 'md', value: `## Step 5: Review on purpose

The review is where planning in your 20s earns its keep. Without it, bets quietly turn into obligations you no longer believe in.

- **Weekly (10 minutes):** did this week's actions happen? What's next week's?
- **When a bet's time box ends:** what did I learn? Double down, change the bet, or drop it?
- **Once a year:** does my direction still feel true? Rewrite it if not.

Changing your plan after a review isn't failing at planning. It's the whole point of it.` },
      { type: 'compare', left: {
        title: "Rigid life plan",
        items: [
          "Fixed 10 year destination",
          "Every area at once",
          "Changing course feels like failure",
          "Rarely reviewed, quietly abandoned",
        ],
      }, right: {
        title: "Plan built for your 20s",
        items: [
          "A direction you can refine",
          "Two or three bets with time boxes",
          "Changing course is part of the plan",
          "Short reviews keep it honest",
        ],
      } },
      { type: 'md', value: `## Common traps

- **Waiting until you know what you want.** You find out by trying things, not by thinking harder. Place a small bet.
- **Copying someone else's timeline.** Their 25 isn't your 25. Plan against your own direction.
- **Planning so much you never start.** If the plan doesn't contain something for this week, it isn't finished.

For longer horizons, the same approach scales. See our guide on [how to make a 5 year plan](/blog/how-to-make-a-5-year-plan).` },
      { type: 'md', value: `## Where to keep it

A notebook works. What matters is that your direction, your bets and this week's actions sit together, so the small stuff stays connected to why you're doing it.

That's what Dmyor is for. You write the direction as a vision, break each bet into milestones and actions on a visual canvas, and work from a daily view that shows only what's actionable today. It's free to start.

You don't need your whole life figured out. You need a direction, a couple of bets, and one thing to do this week.` },
    ],
  },

  /* ───────────── VISION BOARD ALTERNATIVE (SEO) ───────────── */
  {
    title: "A Vision Board Alternative That Turns the Dream Into a Plan",
    slug: "vision-board-alternative",
    date: "October 10, 2026",
    isoDate: "2026-10-10",
    readTime: "6 min read",
    cluster: "Why It Matters",
    ctaLink: "/dashboard/planning",
    ctaText: "Map your vision to actions →",
    keywords: [
      "vision board alternative",
      "vision board alternatives",
      "do vision boards work",
      "vision board doesn't work",
      "action board",
    ],
    excerpt: "A vision board shows you the destination and stops there. Here is an alternative that keeps the picture but adds the path: obstacles, milestones and this week's actions.",
    blocks: [
      { type: 'md', value: `## The problem with vision boards

A vision board is great at one thing: making a goal feel real and exciting. You cut out the house, the trip, the body, the business, and for a moment the future feels close.

Then nothing changes. The board sits on the wall, and a year later most of it is still a picture.

That isn't a lack of willpower. A vision board only answers "what do I want?". It never answers "what do I do on Tuesday?". And the second question is where goals actually live or die.` },
      { type: 'callout', variant: 'note', title: 'What the research suggests', text: "Psychologist Gabriele Oettingen has spent years studying positive fantasies. Her findings suggest that just imagining the happy outcome can feel so good it lowers the energy you put into reaching it. In a well known study by Pham and Taylor, students who pictured the process of studying did better than students who only pictured getting a good grade. Picture the path, not just the prize." },
      { type: 'md', value: `## The alternative: keep the picture, add the path

You don't need to throw your vision board away. You need to finish it. A board that works has four layers, not one:

1. **The vision.** Where you want to be. This is the part you already have.
2. **The obstacle.** What, inside you or around you, is most likely to stop you?
3. **The milestones.** Three to five checkpoints between today and the vision.
4. **This week's actions.** Two or three things small enough to do in one sitting, with a day attached.

Think of it as a vision board that reads left to right: dream on one side, Tuesday on the other.` },
      { type: 'graphic', name: 'planner' },
      { type: 'md', value: `## Step 1: Pick one vision, not twelve

Classic vision boards collect everything you want at once. That's fun to make and impossible to act on. Pick the **one** picture that matters most for the next year or two, and write it as a sentence with a rough date: "Working as a product designer by the end of 2027."

The other pictures can stay. They just aren't this quarter's job.` },
      { type: 'md', value: `## Step 2: Name the obstacle honestly

This is the step Oettingen's method (often called WOOP: Wish, Outcome, Obstacle, Plan) adds, and it's the one most people skip because it's uncomfortable.

Ask: **what in me is most likely to get in the way?** Not the economy, not luck. Things like "I stop when I feel behind", "I scroll after work instead of practicing", "I don't ask for help".

Then write one if-then plan for it: "If I get home tired and want to skip, then I do 15 minutes only, at the desk, before the phone." Tiny, specific, decided in advance.` },
      { type: 'md', value: `## Step 3: Work backwards to milestones

From the vision, ask "what has to be true just before this?" and repeat. For the product designer example:

- Vision: working as a product designer by end of 2027
- Before that: interviews with a portfolio of 3 real case studies
- Before that: 3 finished projects, at least one for a real client
- Before that: core skills in a design tool and one finished practice project

Now the dream has a spine. (If this step feels hard, our guide on [how to break down a big goal](/blog/how-to-break-down-a-big-goal) goes deeper, and [how to make a 5 year plan](/blog/how-to-make-a-5-year-plan) covers longer horizons.)` },
      { type: 'md', value: `## Step 4: Put this week on the board

Only the nearest milestone needs detail. Break it into actions you could finish in one sitting:

- Mon: pick one beginner design course and block 3 evenings for it
- Wed: redesign one screen of an app I use daily
- Sat: post the screen and ask 2 designers for one piece of feedback

If your board has nothing for this week, it's still a wish. If it does, it's a plan.` },
      { type: 'callout', variant: 'note', title: 'Keep looking at it, but differently', text: "When you look at the board, don't just enjoy the vision. Look at the right-hand side: what's this week's action, and is it done? The picture gives you the why. The actions give you the what." },
      { type: 'md', value: `## Doing this in Dmyor

This is exactly the shape Dmyor is built around. You write your vision, and it sits on a visual canvas with milestones branching off it and actions under each milestone, so the dream and the next step live on the same screen. Daily actions and habits (plus timed Focus Mode sessions on Pro) then help you actually do the Tuesday part.

It's free to start. If you already have a vision board, try copying its single most important picture into Dmyor as your vision, and see what the path looks like.` },
    ],
  },
  /* ───────────── HOW TO BREAK DOWN A BIG GOAL (SEO) ───────────── */
  {
    title: "How to Break Down a Big Goal Into Small Steps (That You'll Actually Do)",
    slug: "how-to-break-down-a-big-goal",
    date: "October 9, 2026",
    isoDate: "2026-10-09",
    readTime: "6 min read",
    cluster: "Planning",
    ctaLink: "/dashboard/planning",
    ctaText: "Break down your goal →",
    keywords: [
      "how to break down a big goal",
      "how to break down goals into smaller steps",
      "break big goals into small steps",
      "how to break down a goal into actionable steps",
      "goal breakdown example",
    ],
    excerpt: "A big goal feels heavy because you can't see the first step. Here's a simple way to split any goal until the next action is obvious, with a full example.",
    blocks: [
      { type: 'md', value: `## Why big goals stall

Most big goals don't fail because they're too ambitious. They fail because they stay one big blob. "Start a business", "get fit", "write a book". Every time you think about the goal, your brain has to work out what to do from scratch, and that costs enough energy that you put it off. Again.

The fix isn't more motivation. It's breaking the goal down until the next step is so concrete that starting is easier than not starting.` },
      { type: 'callout', variant: 'note', title: 'The test', text: "You've broken a goal down far enough when the next step can be done in one sitting and you'd know, without debate, when it's done." },
      { type: 'md', value: `## Step 1: Write the goal as a finish line

Before you split anything, make sure there's something to split. "Get fit" has no end. "Run a 10K without stopping by June" does. A finish line gives you something to work backwards from.

Write one sentence: what is true when this goal is done, and roughly by when?` },
      { type: 'md', value: `## Step 2: Ask "what has to be true first?"

Don't ask "what should I do first?". That question invites guessing. Ask instead: **for the finish line to happen, what has to already be true just before it?** Then ask the same question about that answer.

Each answer is a milestone. For most goals you end up with three to five. They're checkpoints, not tasks: "can run 5K comfortably", "have a working prototype", "first draft finished".

Put them in order. Now the big blob is a short path.` },
      { type: 'graphic', name: 'planner' },
      { type: 'md', value: `## Step 3: Split only the nearest milestone

Here's where people overdo it: they try to break every milestone down to the task level on day one. Don't. You'll know far more about milestone three by the time you reach it.

Only the **first** milestone needs detail. Split it into actions until each one passes the test above. If an action makes you think "where would I even start?", it's still a milestone in disguise. Split it again.

Good actions are boring on purpose: "email two people", "buy running shoes", "outline chapter one", "set up the spreadsheet". Boring is good. Boring gets done.` },
      { type: 'md', value: `## A full example

Goal: launch a small online store selling handmade candles. This is an illustration, not a formula.` },
      { type: 'steps', items: [
        { title: "Finish line", text: "The store is live and has made its first 10 sales from people I don't know personally, by March." },
        { title: "Milestone 3", text: "People are finding the store: one channel (Instagram, a local market, or a marketplace) is bringing visitors every week." },
        { title: "Milestone 2", text: "The store is live with 5 products, photos, prices and working checkout." },
        { title: "Milestone 1 (split this one)", text: "I have 5 candle designs I'm happy to sell, with costs worked out." },
        { title: "This week's actions", text: "Order a starter supply kit. Make 3 test candles on Saturday. Write down the cost of each ingredient in one spreadsheet." },
      ] },
      { type: 'callout', variant: 'tip', title: 'Notice what is missing', text: "There's nothing in this week's list about shipping, logos or marketing. Those belong to later milestones. Leaving them for later is what keeps this week doable." },
      { type: 'md', value: `## Step 4: Put the first action on the calendar

An action without a time is still an intention. Pick one or two actions from the list and give them a real slot this week. Small and scheduled beats big and someday.

When you finish an action, take the next one from the same milestone. When the milestone is done, split the next one. That's the whole loop.` },
      { type: 'compare', left: {
        title: "Goal as a blob",
        items: [
          "No clear finish line",
          "Planned forward by guessing",
          "Every step planned on day one, or none",
          "Next step unclear, so it waits",
        ],
      }, right: {
        title: "Goal broken down",
        items: [
          "One sentence finish line",
          "Milestones worked backwards",
          "Only the nearest milestone split into actions",
          "Next step small, obvious and scheduled",
        ],
      } },
      { type: 'md', value: `## When you get stuck

- **The action feels too big:** split it again. "Write the business plan" becomes "list the 5 questions the plan needs to answer".
- **You keep skipping it:** make it smaller, or move it to a time you actually have energy.
- **The milestone is wrong:** change it. A plan you adjust is a plan that's working.

For goals measured in years, the same method works on a bigger scale. We wrote a full guide on [how to make a 5 year plan](/blog/how-to-make-a-5-year-plan) that walks through it. And if your problem is less about planning and more about keeping going after week two, read [how to stick to your goals when motivation runs out](/blog/how-to-stick-to-your-goals).` },
      { type: 'md', value: `## Where to do it

Paper is fine. What matters is that the goal, its milestones and this week's actions sit in one place, so you can always see how the small thing connects to the big one.

That's what Dmyor is built for. You write the goal as a vision, break it into milestones and actions on a visual canvas, and work from a daily view that shows only what's actionable today. It's free to start, and breaking down your first goal takes about one sitting.

Whatever you use, don't stop splitting until you can see what to do this week.` },
    ],
  },
  /* ───────────── HOW TO MAKE A 5 YEAR PLAN (SEO) ───────────── */
  {
    title: "How to Make a 5 Year Plan You'll Actually Follow",
    slug: "how-to-make-a-5-year-plan",
    date: "October 8, 2026",
    isoDate: "2026-10-08",
    readTime: "7 min read",
    cluster: "Planning",
    ctaLink: "/dashboard/planning",
    ctaText: "Build your 5 year plan →",
    keywords: [
      "how to make a 5 year plan",
      "5 year plan template",
      "5 year life plan",
      "how to plan the next 5 years of your life",
      "personal 5 year plan example",
    ],
    excerpt: "Most 5 year plans are a wish list that gets opened once. Here's how to build one that works backwards from the vision to something you do this week.",
    blocks: [
      { type: 'md', value: `## Why most 5 year plans die in a drawer

You've probably made one before. A nice document, maybe a spreadsheet, with columns for career, health, money and relationships. It felt great to write. Then you never opened it again.

That's not because five years is too far away to plan. It's because the plan stopped at the dream. It described where you wanted to be, but it never told you what to do on a normal Wednesday. A plan that never touches your week isn't a plan. It's a mood.

The fix is to build the plan in one direction (from the far future back to today) and to not stop until the bottom of it is something you can start this week.` },
      { type: 'callout', variant: 'note', title: 'The one rule', text: "A 5 year plan is only finished when it contains at least one action you can do in the next seven days. Everything above that is context." },
      { type: 'md', value: `## Step 1: Pick one vision, not seven

Templates usually ask you to fill in every area of life at once: career, finances, fitness, relationships, travel, hobbies. You end up with fourteen goals, each getting a fraction of your attention, and none of them moving.

Instead, ask: **if only one thing changed in the next five years, which one would make the biggest difference to everything else?** Often it's obvious once you ask it that way. A career switch changes your money, your schedule and your confidence. Getting genuinely fit changes your energy for everything else.

Write that one thing as a single sentence, in the present tense, as if it's already true:

- "I work as a product designer at a company I respect."
- "I run my own business that pays me more than my old salary."
- "I'm fluent in Japanese and living in Japan for at least a year."

You can have a second or third vision later. Start with one and make it real enough to act on. Not sure yet what that one thing is? If you're in your 20s, start with a direction and a few bets instead: see [how to plan your life in your 20s](/blog/how-to-plan-your-life-in-your-20s).` },
      { type: 'md', value: `## Step 2: Make it specific enough to be checked

"Be healthier" can never be finished, so it can never be failed, so it never pushes you. In five years, how would you know, without arguing with yourself, that this vision came true?

Add the details that make it checkable: a role, a number, a place, a date. "Run my own business" becomes "run a business that has paid me at least my current salary for twelve months in a row." That second version tells you what to work on. The first one doesn't.

If your vision currently lives on a vision board, keep it. Just add the path underneath: here's [a vision board alternative](/blog/vision-board-alternative) that does exactly that.` },
      { type: 'md', value: `## Step 3: Work backwards in milestones

This is the part that turns a dream into a path. Don't plan forward from today ("what should I do first?"). Plan backward from the finish line:

- **Year 5:** the vision is true.
- **Year 3:** what has to already be true for year 5 to be realistic?
- **Year 1:** what has to be true by this time next year for year 3 to be on track?
- **90 days:** what has to be done in the next three months for year 1 to happen?

Each answer is a milestone: a checkpoint that proves you're getting there. Three to six milestones is plenty. More than that and you're planning instead of doing.

Year 1 is the one to set up first. If you're doing this near the end of the year, our guide on [how to set goals for 2027](/blog/how-to-set-goals-for-2027) shows how to turn that year into a 90 day plan and a first week.` },
      { type: 'graphic', name: 'planner' },
      { type: 'md', value: `## A worked example

Here's what this looks like for one person who wants to switch careers into UX design. It's an illustration, not a formula. Your timeline will look different.` },
      { type: 'steps', items: [
        { title: "Vision (year 5)", text: "I work as a senior product designer on a product I care about." },
        { title: "Milestone (year 3)", text: "Two years of paid design experience, with shipped work I can point to." },
        { title: "Milestone (year 1)", text: "First design job or a steady freelance client, and a portfolio with three real case studies." },
        { title: "Milestone (90 days)", text: "Finish one structured course and complete the first case study from start to finish." },
        { title: "This week's actions", text: "Pick the course and pay for it. Block three evenings on the calendar. Message two working designers to ask what they wish they'd learned first." },
      ] },
      { type: 'callout', variant: 'tip', title: 'Test your bottom layer', text: "Read each action and ask: could I finish this in one sitting, and would I know when it's done? If the answer is no, it's still a milestone pretending to be an action. Split it again." },
      { type: 'md', value: `## Step 4: Break the 90 day milestone into this week

Only the nearest milestone needs to be broken down in detail. Year 3 can stay vague for now; you'll know much more about it in a year. But the next 90 days should be split until you can see this week's work clearly.

Good actions are small and boring: "email three people", "draft the first page", "book the appointment", "set up the spreadsheet". If an action makes you think "where would I even start?", it isn't small enough yet.

Then pick one or two actions and give them a time on your calendar. Unscheduled actions stay as intentions. Scheduled ones tend to happen.

For a longer walkthrough of this step, see [how to break down a big goal](/blog/how-to-break-down-a-big-goal) into milestones and weekly actions.` },
      { type: 'md', value: `## Step 5: Add one habit that keeps the plan alive

Five years is long. Motivation will not last that long, and it isn't supposed to. What lasts is a small repeated behavior that pushes the plan forward even on bad weeks.

Choose one daily or weekly habit that directly serves the vision: thirty minutes of practice, one job application, one workout, one page written. Make it small enough that you can do it on your worst day. The habit carries you through the stretches where the vision feels far away.` },
      { type: 'compare', left: {
        title: "The drawer plan",
        items: [
          "Goals for every area of life at once",
          "Vague outcomes like 'be healthier'",
          "Planned forward, one guess at a time",
          "Ends at the dream, nothing for this week",
          "Opened once a year, if that",
        ],
      }, right: {
        title: "The plan you follow",
        items: [
          "One vision you care about most",
          "A finish line you can check",
          "Planned backward in milestones",
          "Ends in actions you can do this week",
          "Reviewed weekly, adjusted quarterly",
        ],
      } },
      { type: 'md', value: `## Step 6: Review it on a rhythm

A 5 year plan is a living thing. You will learn things in year one that change what year three should look like, and that's the point.

A simple rhythm works:

- **Weekly (10 minutes):** what did I do, what's next, is anything stuck?
- **Every 90 days:** did I hit the milestone? Set the next 90 day milestone and break it down.
- **Yearly:** is this still the vision I want? Rewrite it if not. Changing the plan is not failing the plan.` },
      { type: 'md', value: `## Where to build it

Paper works. A doc works. What matters is that the vision, the milestones and this week's actions live in one place, so you can see how today's small task connects to the big thing.

That's exactly what we built Dmyor for. You write the vision, set its horizon (one to five years), break it into milestones and actions on a visual canvas, and then work from a daily view that only shows what's actionable today. It's free to start, and you can map out your first vision in one sitting. (If you get stuck breaking something down, Dmyor Assist on the Pro plan can suggest next steps.)

Whatever tool you use, don't close the plan until it has something in it for this week. That's the difference between a 5 year plan and a 5 year wish.` },
    ],
  },

  /* ───────────── WHY DREAMS STAY DREAMS (linked from Home) ───────────── */
  {
    title: "Why Your Dreams Stay Dreams (And What Actually Changes That)",
    slug: "why-your-dreams-stay-dreams",
    date: "June 20, 2026",
    isoDate: "2026-06-20",
    readTime: "5 min read",
    cluster: "Why It Matters",
    ctaLink: "/dashboard/planning",
    ctaText: "Break yours down →",
    keywords: [
      "why dreams never happen",
      "stop procrastinating on goals",
      "how to start a big goal",
      "break down a big dream",
      "planning to achieve goals",
    ],
    excerpt: "It's not that you don't want it badly enough. The dream just never got broken into something you could start. Here's the fix.",
    blocks: [
      { type: 'md', value: `## You've carried the same dream for years

The business. The trip. The body. The book. The move. Whatever yours is, you can picture it clearly. You've pictured it for years.

And it still hasn't started.

It's tempting to read that as a willpower problem, or proof you don't want it enough. It's almost never either. The real reason is quieter and far more fixable.` },
      { type: 'callout', variant: 'note', title: 'The actual problem', text: "A dream with no plan isn't a goal. It's a wish with a deadline you keep quietly moving. And a wish gives you nothing to do on a Tuesday." },
      { type: 'md', value: `## You're not the exception, and that's good news

This isn't a personal flaw. Research shows it's the norm. One widely cited analysis found that [roughly 92% of people never achieve the goals they set for themselves](https://www.psychologytoday.com/us/blog/optimizing-success/202412/92-percent-of-people-never-achieve-their-goals). The gap between what people intend and what they actually do is enormous, and almost universal.

Which means the fix isn't trying to be more disciplined than everyone else. It's using a better structure than "just remember to do it."` },
      { type: 'md', value: `## Big isn't the problem. Unbroken is.

Every time you look at the dream, you see the whole thing at once: the finished business, the marathon crossed, the book on the shelf. From where you stand today, that gap is enormous. So your brain does the sensible thing and waits for a better moment to begin.

That moment never comes, because the size never changes. The dream stays exactly as big tomorrow as it is today.

Here's what's really going wrong when a dream never starts:` },
      { type: 'keypoints', items: [
        "It's too big to begin. There's no obvious first move, so you don't make one.",
        "There's no path. Just a start (you, now) and an end (the dream), with fog in between.",
        "Nothing is tracked, so a slow week feels identical to a good one. Momentum is invisible.",
        "Motivation fades. It was always going to. Without structure, there's nothing to catch you when it does.",
      ] },
      { type: 'md', value: `## The fix is almost insultingly simple

You don't need more motivation, a vision board, or the perfect moment. You need to break the dream down until the first step is small enough to actually take.

That's the entire move. Take the mountain and turn it into a staircase.` },
      { type: 'graphic', name: 'planner' },
      { type: 'steps', items: [
        { title: "Write the dream down", text: "One honest sentence. It leaves your head and becomes a thing you can work on." },
        { title: "Give it a real horizon", text: "One to five years. Not 'someday'. A finish line your future self can be measured against." },
        { title: "Break it into milestones", text: "The few big checkpoints that prove you're getting there." },
        { title: "Shrink milestones into a first action", text: "Small enough to start this week. If it scares you, it's still too big." },
      ] },
      { type: 'callout', variant: 'tip', title: 'The first step is meant to be small', text: "The real first step toward a dream is almost always smaller and more boring than the dream itself. 'Outline one chapter.' 'Email three gyms.' 'Open the spreadsheet.' That's not a letdown. That's the whole secret." },
      { type: 'md', value: `## Dreams don't die from lack of wanting

They die in the gap between wanting and starting. Closing that gap isn't about trying harder. It's about giving the dream a structure: a horizon, a path, and a first step you can take today.

Pick how many years you're willing to give it. Break it down. Take the small step. That's how a dream finally stops being a dream.` },
    ],
  },

  /* ───────────────── 0. PINNED · EMOTIONAL FLAGSHIP ───────────────── */
  {
    title: "What If You Gave Your All to Make Your Dreams Real?",
    slug: "what-if-you-gave-your-all-to-make-your-dreams-real",
    date: "June 15, 2026",
    isoDate: "2026-06-15",
    readTime: "6 min read",
    cluster: "Why It Matters",
    pinned: true,
    ctaLink: "/dashboard/planning",
    ctaText: "Begin your vision →",
    keywords: [
      "make your dreams real",
      "give life your all",
      "design your future",
      "document your journey",
      "five year vision",
      "live with no regrets",
    ],
    excerpt: "You already carry the dream. The only question left is whether you build it, or let it quietly fade like the last one did. This is about choosing to build.",
    blocks: [
      { type: 'md', value: `## You already know the dream

You don't need anyone to tell you what you want. You've felt it for years — in the quiet moments, on the long drives, the night before something changes. A clearer version of your life. Who you'd be. What you'd have built. How it would feel to wake up inside it.

And you know the other thing too. How that picture has a way of staying exactly where it is. In your head. Unwritten. Perfectly safe, and slowly fading — until one day it's just something you used to want.

It didn't fade because you failed. It faded because you never started. The thought stayed a thought, and thoughts without a build plan don't become lives.

So here's the real question, the only one that matters: **what if, this time, you actually gave it everything?**` },
      { type: 'md', value: `## The time is going either way

Five years from now, you will be somewhere. Living somewhere, doing some kind of work, being some version of yourself. That part isn't in question.

The only question is whether that version of you was *built* — or whether it just accumulated while you waited for the right moment that never quite arrived.

You're going to spend those years alive regardless. Working, sleeping, scrolling, existing. The clock runs whether you use it or not. The worst case of trying is that you reached and fell a little short. The best case is the exact life you designed. Both beat never having started.` },
      { type: 'callout', variant: 'note', title: 'The whole idea', text: "A serious, all-out effort to get the life you actually want — written down, built toward, and documented every step of the way, so that even the trying becomes something real." },
      { type: 'md', value: `## The dream is the easy part. The work is where it gets real.

Wanting something costs nothing. Everybody wants. What separates the life you imagine from the life you get is whether the dream ever gets broken down into something your hands can hold.

That's the first thing Dmyor does. You write the vision at the top — the real one, with a date on it. Then you break it down: into milestones that prove you're getting there, into actions small enough to start this afternoon. The mountain becomes a path. And a path, you can walk.` },
      { type: 'graphic', name: 'planner' },
      { type: 'md', value: `## Then you show up. Day after ordinary day.

No dream is reached in one heroic week. It's reached in the unglamorous middle — the mornings you'd rather skip, the reps nobody claps for, the small ships that feel like nothing until they quietly become everything.

Dmyor is built for that middle. Execute hands you the few things that matter *today*, so you never drown in the size of it all. Habits track the daily reps that compound while you're not watching. The plan stops being a wish on a page and becomes something you move, a little, every single day.` },
      { type: 'compare', left: {
        title: "The dream left in your head",
        items: [
          "Perfect, possible, and never tested",
          "No date, no edges, no first step",
          "Fades a little more each year",
          "No record that you ever even wanted it",
        ],
      }, right: {
        title: "The dream you build on Dmyor",
        items: [
          "Written down and broken into real steps",
          "Worked a little every day",
          "Visible progress you can stand on",
          "A permanent record of you trying",
        ],
      } },
      { type: 'md', value: `## And the journey becomes something you keep forever

Here's the part most tools forget. The results matter — but so does the story of getting there. The night it almost fell apart. The week it finally clicked. Why you made the call you made, while you still remembered.

Dmyor keeps all of it. Your Journal holds the build in your own words. Your Library holds everything you learned. Years from now you'll open this vision and find not just *that* you did it — but the whole human story of *how*. Written while you were in it. Timestamped. Permanent.

That's what it means to make a vision exist forever. Not a screenshot. A record of a real person, wanting something, and going after it with everything they had.` },
      { type: 'graphic', name: 'journal' },
      { type: 'callout', variant: 'tip', title: 'For the one who comes after', text: "Your future self will read what you write here — and so might anyone who wants to understand what it was like to be alive right now, wanting something and building toward it. Don't let five years pass without leaving the record." },
      { type: 'md', value: `## So — what if?

What if you wrote it down today? What if you broke it into pieces and started the first one? What if you gave the next five years a serious, all-out, no-excuses effort to become the person you keep imagining?

You already have the dream. Dmyor gives it a place to become real — and a record that lasts long after.

The years are going either way. Spend them on purpose.` },
    ],
  },

  /* ───────────────────────── 1. FLAGSHIP ───────────────────────── */
  {
    title: "How Dmyor Works: One Vision, Five Modes",
    slug: "how-dmyor-works-one-vision-five-modes",
    date: "June 14, 2026",
    isoDate: "2026-06-14",
    readTime: "5 min read",
    cluster: "How It Works",
    ctaLink: "/dashboard/planning",
    ctaText: "Open your workspace →",
    keywords: [
      "how dmyor works",
      "life planning app",
      "vision planning",
      "execute daily tasks",
      "habit tracker",
    ],
    excerpt: "A vision is useless until it becomes a tree, a daily list, and a set of reps. Dmyor gives you five modes that do exactly that.",
    blocks: [
      { type: 'md', value: `## Most planning tools stop exactly where the hard part begins

You've felt this. You write down a goal — "launch a SaaS," "get in the best shape of my life," "finally write the book" — and for about a day it feels like progress. You were honest with yourself. You named the thing. But a week later nothing has actually changed, because a goal sitting on its own has no edges. You can't *do* "launch a SaaS." There's nowhere to put your hands.

Dmyor is built on a single belief: **a vision only becomes real when it moves through stages.** First you break it down. Then you work it daily. Then you keep showing up. Each of those stages is a mode you can open, and together they're the whole workspace.

Here's how the five modes fit together.` },
      { type: 'graphic', name: 'pillarmap' },
      { type: 'md', value: `## What each mode is actually for

You don't need all five on day one. But it helps to know what's waiting for you as your vision grows.` },
      { type: 'keypoints', items: [
        "Planning — where you break the vision into a tree of milestones and actions, splitting each piece until it's small enough to actually start.",
        "Execute — one focused page that pulls every doable item out of the tree into a today-list, so you're never staring at an 18-month map on a Tuesday.",
        "Habits — the recurring reps under your plan, tracked with streaks, because milestones get hit by what you repeat, not by what you intend.",
        "Library — documents and notes attached directly to the nodes they belong to, so your research lives where you'll actually use it.",
        "Journal — a dated, plain-language record of the build in your own words, written for the version of you who'll want to remember how this went.",
      ] },
      { type: 'callout', variant: 'note', title: 'The one-line version', text: 'Planning decides what to do. Execute makes you do what matters today. Habits make sure you keep showing up. Library and Journal quietly hold everything you learn on the way.' },
      { type: 'md', value: `## Where to actually start

Don't try to set the whole thing up at once — that's just planning-as-procrastination in a nicer outfit.

Create one vision. Give it three milestones. Then tomorrow morning, open Execute and do the first small thing. That tiny loop — plan a little, do a little — is the entire system. Everything else is depth you reach for only when you need it.` },
    ],
  },

  /* ───────────────────────── 2. PLANNING ───────────────────────── */
  {
    title: "Planning: Turn a Vision Into a Tree You Can Execute",
    slug: "planning-turn-a-vision-into-a-tree",
    date: "June 12, 2026",
    isoDate: "2026-06-12",
    readTime: "4 min read",
    cluster: "Planning",
    ctaLink: "/dashboard/planning",
    ctaText: "Start planning →",
    keywords: [
      "how to plan a goal",
      "break down a goal",
      "milestones and actions",
      "goal tree",
      "planning method",
    ],
    excerpt: "Big goals fail because they stay big. Planning mode forces a vision down into pieces small enough to act on.",
    blocks: [
      { type: 'md', value: `## A vision is a claim. A tree is what makes it doable.

"Launch a profitable SaaS in 18 months" is a direction, and directions are great for deciding where to point yourself. But you can't sit down and *do* a direction. The reason big goals quietly die isn't laziness — it's that they stay big. They never get broken into anything your Tuesday self can pick up.

Planning mode is where you take that one big claim and keep splitting it until the bottom of the tree is made of things you could honestly start this afternoon.` },
      { type: 'graphic', name: 'planner' },
      { type: 'md', value: `## Building the tree, step by step

It's the same four moves every time, whether you're planning a company or a comeback.` },
      { type: 'steps', items: [
        { title: "Write the vision", text: "One sentence at the top. The destination, with a time horizon attached so it stays honest." },
        { title: "Add your milestones", text: "The three to six checkpoints that would prove you're actually getting there. These are meaningful chunks, not chores — 'ship the MVP,' not 'reply to that email.'" },
        { title: "Break each milestone into actions", text: "Concrete things with an obvious finished state. If you can't picture the moment you'd call it done, it's still too big — keep splitting." },
        { title: "Go deeper only where it earns it", text: "A few actions need sub-steps. Most don't. Dmyor never forces extra layers on you — depth is something you add where the work is genuinely tangled, and nowhere else." },
      ] },
      { type: 'callout', variant: 'tip', title: 'A simple test', text: "If looking at a node makes you think 'where would I even start?' — that's the node telling you it isn't small enough yet. Split it until starting feels obvious." },
      { type: 'md', value: `## Status flows down the tree

Here's a small thing that saves a lot of annoyance: when you mark a milestone **active** or **done**, its children follow automatically.

Each state has its own color so you can read the whole plan in one glance — **standby is blue, active is orange, done is green.** Flip a milestone to done and you won't find its actions stranded in the wrong state underneath it. The tree always tells you the truth about where things stand.` },
    ],
  },

  /* ───────────────────────── 3. EXECUTE ───────────────────────── */
  {
    title: "Execute: The One Page That Tells You What to Do Today",
    slug: "execute-one-page-what-to-do-today",
    date: "June 10, 2026",
    isoDate: "2026-06-10",
    readTime: "4 min read",
    cluster: "Execute",
    ctaLink: "/dashboard/planning",
    ctaText: "Open Execute →",
    keywords: [
      "daily execution",
      "what to do today",
      "focus on tasks",
      "productivity system",
      "todo from goals",
    ],
    excerpt: "Planning tells you what to do. Execute strips away everything except what you do today.",
    blocks: [
      { type: 'md', value: `## A tree is for thinking. A list is for doing.

Your plan is a beautiful, branching map — and that's exactly the wrong thing to look at when you've got ninety focused minutes before a meeting. Nobody opens an 18-month tree on a Tuesday morning and feels ready to move. You don't need the map. You need the next few things, in front of you, with nothing else competing for the space.

Execute mode quietly flattens your entire tree into one page: every item that's actually doable, gathered in one place, ready to ship.` },
      { type: 'graphic', name: 'execute' },
      { type: 'md', value: `## Why a dedicated "today" page beats working off the tree

You *could* just work straight from the plan. Most people try. Here's why it falls apart, and what changes when today gets its own surface.` },
      { type: 'compare', left: {
        title: "Working from the tree",
        items: [
          "You re-navigate the whole plan every day",
          "Important small tasks hide under milestones",
          "Easy to drift into planning instead of doing",
          "No single surface for 'today'",
        ],
      }, right: {
        title: "Working from Execute",
        items: [
          "One page, every actionable item",
          "Pin what matters to the top",
          "Check it off, log what you did",
          "Stale items surface so nothing rots",
        ],
      } },
      { type: 'callout', variant: 'note', title: 'Pin your non-negotiables', text: "Star the two or three things that genuinely matter today. They rise to a focus strip at the top; everything else stays available but out of your face. You make the focus decision once in the morning, then you just work — no re-deciding every time you glance at the screen." },
      { type: 'md', value: `## Log it as you go

When you finish something, you can log a quick note about what happened straight back to the plan. Future you, opening this vision in three months, will see not just that it got done — but how. That running trail is what turns a checklist into a story you can actually learn from.` },
    ],
  },

  /* ───────────────────────── 4. HABITS ───────────────────────── */
  {
    title: "Habits: The Daily Reps That Make a Plan Inevitable",
    slug: "habits-daily-reps-make-a-plan-inevitable",
    date: "June 8, 2026",
    isoDate: "2026-06-08",
    readTime: "4 min read",
    cluster: "Habits",
    ctaLink: "/dashboard/planning",
    ctaText: "Set up habits →",
    keywords: [
      "habit tracker",
      "build consistency",
      "daily habits for goals",
      "streak tracking",
      "discipline system",
    ],
    excerpt: "Milestones get hit by the boring things you repeat. Habits mode tracks the reps that compound.",
    blocks: [
      { type: 'md', value: `## Goals are hit by habits, not by heroic weeks

Think about any milestone that actually got reached in your life. It almost never came from one dramatic, all-nighter sprint. It came from something boring you did over and over — the runs, the pages, the reps, the showing up — until one day the result was just *there*.

A milestone like "first 10 paying customers" works the same way. Underneath it are the unglamorous daily reps: the outreach, the follow-ups, the small ships. Habits mode tracks exactly those reps and ties each one back to the part of the plan it serves, so consistency stops being a feeling and becomes something you can see.` },
      { type: 'graphic', name: 'habits' },
      { type: 'md', value: `## What keeps the habit tracker honest

A habit tracker only works if it refuses to lie to you. Here's how this one stays straight.` },
      { type: 'keypoints', items: [
        "Every habit links to a node in your plan, so you're never grinding a routine you've forgotten the reason for — the 'why' is one tap away.",
        "Schedule it the way it actually lives: every day, specific weekdays, or a looser X-times-a-week target.",
        "Streaks show your momentum, and a weekly grid shows the unvarnished truth of the last seven days at a glance.",
        "Miss a day and the streak breaks — no quiet fudging, no 'close enough.' That honesty is the entire point of tracking at all.",
      ] },
      { type: 'callout', variant: 'tip', title: 'Make it absurdly small', text: "The habit you can do on your worst, most exhausted day will always beat the ambitious one you only manage at your best. Shrink it until skipping feels like more effort than just doing it." },
    ],
  },

  /* ───────────────────── 5. LIBRARY + JOURNAL ───────────────────── */
  {
    title: "Library and Journal: Where Your Thinking and Story Live",
    slug: "library-and-journal-where-thinking-lives",
    date: "June 6, 2026",
    isoDate: "2026-06-06",
    readTime: "4 min read",
    cluster: "Workspace",
    ctaLink: "/dashboard/planning",
    ctaText: "Open your workspace →",
    keywords: [
      "notes for goals",
      "project documents",
      "progress journal",
      "knowledge base",
      "document your journey",
    ],
    excerpt: "Plans need research and reflection. Library holds the docs. Journal holds the story.",
    blocks: [
      { type: 'md', value: `## A plan is more than its checkboxes

As you work toward something real, you accumulate two kinds of things that a task list has no room for. You gather **knowledge** — the research, the decisions, the stuff you figured out the hard way. And you live through a **story** — the wins, the dead ends, how a stretch actually felt while you were in it.

If that material has nowhere to go, you lose it. Dmyor gives it two homes: Library for what you know, Journal for what you went through.` },
      { type: 'md', value: `## Library: documents that live where you'll use them

The problem with a generic "notes" folder is that the note and the work drift apart. You write up pricing research, and three weeks later it's buried under forty other files when you finally need it.

Library fixes that by attaching documents and notes straight to the nodes they belong to. Your pricing research sits on the pricing action. The competitor teardown hangs off the milestone it informs. And because one doc can link to many nodes, a single piece of thinking can quietly support every part of the plan it touches.` },
      { type: 'graphic', name: 'library' },
      { type: 'md', value: `## Journal: the build, in your own words

The Journal isn't tasks and it isn't status. It's a dated, plain-language space where you just write down how it's actually going — the same WYSIWYG editor as your documents, with a quiet line at the top reminding you who it's for.` },
      { type: 'graphic', name: 'journal' },
      { type: 'callout', variant: 'note', title: 'Who you\'re really writing for', text: "Three months from now you won't remember why you made that call, or how heavy a particular week felt. The Journal is a letter to that future version of you — written while you still know the answers." },
      { type: 'md', value: `## Together, they make a vision durable

Library holds what you learned. Journal holds what you lived. Put them under a plan and the whole thing stops being a brittle to-do list you'd be a little embarrassed to reopen — and becomes a real body of work you can come back to, learn from, and be glad you kept.` },
    ],
  },
];

export default blogs;
